import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { distinctUntilChanged, filter, tap } from 'rxjs';

import { BaseAuthComponent } from '../../abstract-components/base-auth.component';
import { AUTH_STATE_TOKEN } from '~console/app/~shared/constants/global.constants';
import {
  enforceDigitsOnly,
  matchFieldsValidator,
  phoneNumberValidator,
  strongPasswordValidator,
  validatePhoneNumber
} from '~console/app/~shared/utils/validators';
import { getErrorMessage } from '~console/app/~shared/utils/utils';
import { DialogService } from '~console/app/~shared/services/dialog.service';
import { ToastService } from '~console/app/~shared/services/toast.service';
import { RegistrationOtpModalComponent } from '../../modals/registration-otp-modal/registration-otp-modal.component';

@Component({
  selector: 'email-confirmation-form',
  standalone: false,
  templateUrl: './email-confirmation-form.component.html',
  styleUrl: './email-confirmation-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EmailConfirmationFormComponent
  extends BaseAuthComponent
  implements OnInit
{
  private _toastService = inject(ToastService);
  private _dialogService = inject(DialogService);

  validatePhoneNumberFn = validatePhoneNumber;

  violations$ = this._globalFacade.formViolations$.pipe(
    filter(Boolean),
    tap({
      next: violations => {
        violations.forEach(violation => {
          const control = violation.propertyPath.includes('password')
            ? this.password || this.confirmPassword
            : violation.propertyPath.includes('otpCode') ||
                violation.propertyPath.includes('otp') ||
                violation.propertyPath.includes('token')
              ? this.otpCode
              : null;
          if (control) {
            control.setErrors({ httpError: violation.title });
            this._cdRef.detectChanges();
          }
        });
      }
    })
  );

  emailConfirmationForm = new FormGroup({
    lastName: new FormControl('', {
      validators: [Validators.required, Validators.minLength(3)],
      nonNullable: true
    }),
    firstName: new FormControl('', {
      validators: [Validators.required, Validators.minLength(3)],
      nonNullable: true
    }),
    phoneNumber: new FormControl('', {
      validators: [
        Validators.required,
        phoneNumberValidator({
          country: this._constantsService.defaultCountry
        })
      ],
      nonNullable: true
    }),
    otpCode: new FormControl(''),
    password: new FormControl(''),
    confirmPassword: new FormControl('')
  });

  registrationFormValues$ = this._authFacade.registrationFormValues$;
  isRegisteringUser$ = this._authFacade.isRegisteringUser$;
  isLoggingIn$ = this._authFacade.isLoggingIn$;
  registrationOtp$ = this._authFacade.registrationOtp$;

  statePropertyPath = `${AUTH_STATE_TOKEN.getName()}.registration.emailConfirmationFormValues`;

  isUserKnown$ = this._authFacade.isUserKnown$;

  ngOnInit(): void {
    this.isUserKnown$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        distinctUntilChanged(),
        tap(isUserKnown => {
          this._updateFormValidation(isUserKnown);
        })
      )
      .subscribe();

    this._listenToRegistrationTwoFaChallenge();
  }

  private _listenToRegistrationTwoFaChallenge(): void {
    this._eventService.events$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        filter(event => event.type === 'OPEN_REGISTRATION_2FA_MODAL'),
        tap(() => this._openRegistrationTwoFaModal())
      )
      .subscribe();
  }

  private _openRegistrationTwoFaModal(): void {
    this._dialogService.open({
      component: RegistrationOtpModalComponent,
      config: {
        panelClass: ['mat-dialog-custom', 'w-[520px]'],
        maxWidth: '520px',
        disableClose: true
      }
    });
  }

  private _updateFormValidation(isUserKnown: boolean | undefined): void {
    this.emailConfirmationForm.clearValidators();
    this.password?.clearValidators();
    this.confirmPassword?.clearValidators();
    this.otpCode?.clearValidators();
    this.lastName?.clearValidators();
    this.firstName?.clearValidators();
    this.phoneNumber?.clearValidators();

    if (isUserKnown) {
      this.lastName?.setValue(this.lastName.value ?? '', {
        emitEvent: false
      });
      this.firstName?.setValue(this.firstName.value ?? '', {
        emitEvent: false
      });
      this.phoneNumber?.setValue(this.phoneNumber.value ?? '', {
        emitEvent: false
      });
      this.confirmPassword?.setValue('');
      this.confirmPassword?.updateValueAndValidity({ emitEvent: false });
      this.password?.setValidators([
        Validators.required,
        Validators.minLength(8),
        strongPasswordValidator
      ]);
    } else {
      this.lastName?.setValidators([Validators.required]);
      this.firstName?.setValidators([Validators.required]);
      this.phoneNumber?.setValidators([
        Validators.required,
        phoneNumberValidator({
          country: this._constantsService.defaultCountry
        })
      ]);
      this.password?.setValidators([
        Validators.required,
        Validators.minLength(8),
        strongPasswordValidator
      ]);
      this.confirmPassword?.setValidators([
        Validators.required,
        Validators.minLength(8)
      ]);
      this.otpCode?.setValidators([
        Validators.required,
        Validators.pattern(/^\d{6}$/)
      ]);

      this.emailConfirmationForm.setValidators(
        matchFieldsValidator('password', 'confirmPassword')
      );
    }

    this.lastName?.updateValueAndValidity({ emitEvent: false });
    this.firstName?.updateValueAndValidity({ emitEvent: false });
    this.phoneNumber?.updateValueAndValidity({ emitEvent: false });
    this.password?.updateValueAndValidity({ emitEvent: false });
    this.confirmPassword?.updateValueAndValidity({ emitEvent: false });
    this.otpCode?.updateValueAndValidity({ emitEvent: false });
    this.emailConfirmationForm.updateValueAndValidity({ emitEvent: false });
  }

  registerUser(): void {
    this._authFacade
      .registerUser(this.otpCode?.value)
      .pipe(
        tap({
          error: error => {
            const errorMessage = getErrorMessage(error);
            if (errorMessage.trim()) {
              this._toastService.open({
                content: errorMessage,
                type: 'error'
              });
            }
          }
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe();
  }

  get otpCode(): AbstractControl | null {
    return this.emailConfirmationForm.get('otpCode');
  }

  get password(): AbstractControl | null {
    return this.emailConfirmationForm.get('password');
  }

  get confirmPassword(): AbstractControl | null {
    return this.emailConfirmationForm.get('confirmPassword');
  }

  onResendOtp(): void {
    this._authFacade.getRegistrationOneTimePassword();
  }

  onOtpKeydown(event: KeyboardEvent): void {
    enforceDigitsOnly(event);
  }

  get lastName(): AbstractControl | null {
    return this.emailConfirmationForm.get('lastName');
  }

  get firstName(): AbstractControl | null {
    return this.emailConfirmationForm.get('firstName');
  }

  get phoneNumber(): AbstractControl | null {
    return this.emailConfirmationForm.get('phoneNumber');
  }
}
